import { useState, useCallback } from 'react'

export interface SearchbarFields {
    adults: number | null
    kids: number | null
    arrival: Date | string | null
    departure: Date | string | null
    location: string
}

export interface SearchbarFieldsReturn {
    adults: number
    kids: number
    arrival: Date | null
    departure: Date | null
    location: string
}

type DateFields = 'arrival' | 'departure'
type NumberFields = 'adults' | 'kids'
type StringFields = 'location'

type UpdateFieldValue<T extends keyof SearchbarFields> = T extends DateFields
    ? Date | string | null
    : T extends NumberFields
    ? number
    : T extends StringFields
    ? string
    : never

export type UpdateSearchBarTransport = <T extends keyof SearchbarFields>(field: T, value: UpdateFieldValue<T>) => void

interface UseSearchbarReturn {
    searchbar: SearchbarFieldsReturn
    updateSearchbar: UpdateSearchBarTransport
}

const DEFAULT_VALUES: SearchbarFieldsReturn = {
    adults: 2,
    kids: 0,
    arrival: null,
    departure: null,
    location: '',
} as const

const convertToDate = (value?: Date | string | null): Date | null => {
    if (!value) {
        return null
    }
    return value instanceof Date ? value : new Date(value)
}

const useSearchbar = (initialFields: Partial<SearchbarFields> = {}): UseSearchbarReturn => {
    const [searchbar, setSearchbar] = useState<SearchbarFieldsReturn>(() => ({
        ...DEFAULT_VALUES,
        ...{
            arrival: convertToDate(initialFields.arrival),
            departure: convertToDate(initialFields.departure),
            adults: initialFields.adults ?? DEFAULT_VALUES.adults,
            kids: initialFields.kids ?? DEFAULT_VALUES.kids,
            location: initialFields.location ?? DEFAULT_VALUES.location,
        },
    }))

    const updateSearchbar = useCallback(<T extends keyof SearchbarFields>(field: T, value: UpdateFieldValue<T>) => {
        setSearchbar(prev => ({
            ...prev,
            [field]:
                field === 'arrival' || field === 'departure' ? convertToDate(value as Date | string | null) : value,
        }))
    }, [])

    return {
        searchbar,
        updateSearchbar,
    }
}

export default useSearchbar
